import React from 'react';
import { cn, initials, isOnline, roleLabel } from './model/derive';
import { useWorkboardCtx } from './model/useWorkboard';

/**
 * Person chip (DS v2 · 1022) — 아바타(이니셜) + 이름 + 접속 점(실제 접속).
 *   S = 아바타 24 · 이름 12 · 점 8 (카드 · 리스트 행)   L = 아바타 32 · 이름 14/500 · 점 10 (스윔레인 행 머리)
 *   여러 명이면 아바타 2개까지 -8px 겹치고 나머지는 +N, 이름은 first name 을 쉼표로.
 *   보드 카드 안에서는 접속 점을 빼고(dot={false}), 리스트 행 · 스윔레인 행 머리는 점을 보인다.
 * Tooltip: "{Full name} · {Role}"
 */
const SIZE = {
  S: {
    box: 'h-6 w-6 text-[10px]',
    name: 'text-[12px] text-text-secondary',
    dot: 'h-2 w-2',
    ring: 2,
  },
  L: {
    box: 'h-8 w-8 text-[12px]',
    name: 'text-[14px] font-medium text-text-primary',
    dot: 'h-2.5 w-2.5',
    ring: 2,
  },
};

const firstName = (name = '') => name.trim().split(/\s+/)[0] ?? name;

const PersonChip = ({ members = [], size = 'S', dot = true, className }) => {
  const { now, data } = useWorkboardCtx();
  const list = members.filter(Boolean);
  if (!list.length) return null;
  const s = SIZE[size] ?? SIZE.S;
  const shown = list.slice(0, 2);
  const more = list.length - shown.length;
  const name = list.length === 1 ? list[0].name : list.map((m) => firstName(m.name)).join(', ');
  const single = list.length === 1 ? list[0] : null;
  const online = single && isOnline(single, now, data.viewerId);

  return (
    <span
      title={list.map((m) => `${m.name} · ${roleLabel(m) || 'Team lead'}`).join('\n')}
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
    >
      <span className='flex shrink-0 items-center'>
        {shown.map((m, i) => (
          <span
            key={m.id}
            className={cn(
              'relative flex shrink-0 items-center justify-center rounded-full bg-surface-green10 font-medium text-green-deep04 shadow-[0_0_0_2px_#ffffff]',
              s.box,
              i > 0 && '-ml-2',
            )}
          >
            {initials(m.name)}
            {/* 접속 점 — 한 명일 때만 (팀장 · 이탈자 제외) */}
            {dot && single && m.role !== 'npc' && m.status !== 'dropped' && (
              <span
                aria-label={online ? 'Online' : 'Offline'}
                className={cn(
                  'absolute -bottom-px -right-px rounded-full',
                  s.dot,
                  online
                    ? 'bg-functional-success03 shadow-[0_0_0_2px_#ffffff]'
                    : 'bg-grayscale-gray00 shadow-[inset_0_0_0_2px_theme(colors.grayscale.gray04)]',
                )}
              />
            )}
          </span>
        ))}
        {more > 0 && (
          <span
            className={cn(
              '-ml-2 flex shrink-0 items-center justify-center rounded-full bg-grayscale-gray01 font-medium text-text-secondary shadow-[0_0_0_2px_#ffffff]',
              s.box,
            )}
          >
            +{more}
          </span>
        )}
      </span>
      <span className={cn('truncate leading-[140%]', s.name)}>{name}</span>
    </span>
  );
};

const flowName = (m) => (m.role === 'npc' ? 'Team lead' : firstName(m.name));

/** 보드 카드의 담당자 → 받는 사람 */
export const HandoffFlow = ({ assignee, receiver, className }) => {
  if (!assignee && !receiver) return null;
  const person = (m, tone) =>
    m && (
      <span className='flex min-w-0 items-center gap-1.5'>
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium',
            tone === 'assignee'
              ? 'bg-surface-green10 text-green-deep04'
              : 'bg-grayscale-gray01 text-text-secondary shadow-[inset_0_0_0_1px_theme(colors.grayscale.gray03)]',
          )}
        >
          {initials(m.name)}
        </span>
        <span className='truncate text-[12px] leading-[140%] text-text-secondary'>
          {flowName(m)}
        </span>
      </span>
    );
  return (
    <span
      title={[
        assignee && `Assignee: ${assignee.name}`,
        receiver && `Receiver: ${receiver.name}`,
      ]
        .filter(Boolean)
        .join('\n')}
      className={cn('inline-flex min-w-0 items-center gap-1.5', className)}
    >
      {person(assignee, 'assignee')}
      <span aria-hidden className='shrink-0 text-[12px] text-text-tertiary'>
        →
      </span>
      {person(receiver, 'receiver')}
    </span>
  );
};

export default PersonChip;
